import { useSelector } from 'react-redux';
import { allowFormObjectAddOrRemove, formObjectFieldsState } from '@/forms/objects/helper';
import { saveStates } from '@/constants/lifecycle';
import { safeFormObjects } from '@/forms/state/state';
import { resolveCatalogueTemplate } from '../../catalogue/helper';
import { datasetPermissions } from '../../sharing/constants';
import { canEditDataset, datasetIsEditing } from '../../helper';
import { datasetPerms } from '../../sharing/state/state';
import { disableDatasetEdit } from '../../status/helper';

const editDatasetState = (dataset, template, update) => ({
  canEdit: update && dataset.permissions[datasetPermissions.UPDATE],
  dataset,
  isEditing: datasetIsEditing(dataset),
  saveErrors: dataset.saveErrors,
  saveStatus: dataset.saveStatus,
  template,
});

const useCatalogueFieldList = ({ category, code, section }) => useSelector(({
  auth: { role: { permissions: { datasets: { update } } } },
  catalogues: { templates },
  datasets: { datasets },
}) => {
  const { dataset, template } = resolveCatalogueTemplate(datasets, templates, { category, code });
  return { ...editDatasetState(dataset, template, update), fields: template.sections[section].fields };
});

const useFormFields = ({ category, code }) => useSelector(({
  auth: { role: { permissions: { datasets: { update } } } },
  catalogues: { templates },
  datasets: { datasets },
}) => {
  const { dataset, template } = resolveCatalogueTemplate(datasets, templates, { category, code }, {
    isEditing: false,
    ...saveStates,
    permissions: datasetPerms(),
  });

  return editDatasetState(dataset, template, update);
});

const useCatalogueObjectFields = () => useSelector(({
  auth: { role },
  datasets: { datasets, catalogueFieldItems },
  catalogues: { templates },
}) => {
  const { template } = resolveCatalogueTemplate(datasets, templates, catalogueFieldItems);

  return {
    canEdit: canEditDataset(catalogueFieldItems, role),
    ...catalogueFieldItems,
    ...formObjectFieldsState(template, catalogueFieldItems),
    template,
  };
});

const useCatalogueLinksList = ({ category, code }) => useSelector(({
  auth: { role },
  catalogues: { templates },
  datasets: { datasets },
}) => {
  const { dataset, template } = resolveCatalogueTemplate(datasets, templates, { category, code });
  return { canEdit: canEditDataset(dataset, role), template };
});

const useCatalogueObjectsTable = ({
  category,
  code,
  field,
  items,
}) => useSelector(({
  datasets: { datasets },
  catalogues: { templates },
  auth: { role },
}) => {
  const { dataset, template } = resolveCatalogueTemplate(datasets, templates, { category, code });
  const disabled = !dataset || disableDatasetEdit(dataset);

  return {
    ...allowFormObjectAddOrRemove(field, safeFormObjects(items), disabled),
    canEdit: canEditDataset(dataset, role),
    template,
  };
});

const useDatasetFormDialogs = () => useSelector(({
  auth: { role: { permissions: { datasets: { read } } } },
  datasets: {
    catalogueFieldItems: { linksOpen, open },
  },
}) => ({
  allowLinks: linksOpen && read,
  allowObjects: open && read,
}));

export default {
  useCatalogueFieldList,
  useCatalogueLinksList,
  useCatalogueObjectFields,
  useCatalogueObjectsTable,
  useDatasetFormDialogs,
  useFormFields,
};
